/**
 * Utility for safe operations that may fail
 */

type SafeResult<T> = { success: true; value: T } | { success: false; error: unknown };

/**
 * Logs errors in development mode only
 * @param { string } context - Description of the operation
 * @param { unknown } error - The error that occurred
 */
const logErrorInDev = (context: string, error: unknown): void => {
    if (process.env.NODE_ENV === 'development') {
        console.warn(`[AutoSave] ${context}:`, error);
    }
};

/**
 * Safely executes a function that may throw, returning a result object instead
 * @param { () => {} } fn - Function to execute
 * @param { string } context - Description for logging
 * @returns { SafeResult } - Result object with success status
 */
export const safeExecute = <T>(fn: () => T, context: string): SafeResult<T> => {
    try {
        const value = fn();
        return { success: true, value };
    } catch (error) {
        logErrorInDev(context, error);
        return { success: false, error };
    }
};

/**
 * Safely sets data in storage, returns success status
 * @param { (data: T) => void } setter - Function to set data
 * @param { T } data - Data to set
 * @param { string } context - Description for logging
 * @returns { boolean } - true if successful, false otherwise
 */
export const safeSetStorage = <T>(setter: (data: T) => void, data: T, context: string): boolean => {
    const result = safeExecute(() => setter(data), context);
    return result.success;
};

/**
 * Safely clears storage by setting it to undefined
 * @param { (data: T | undefined) => void } setter - Function to clear data (e.g., from useStore)
 * @param { string } context - Description for logging
 * @returns { boolean } - true if successful, false otherwise
 */
export const safeClearStorage = <T = undefined>(setter: (data: T | undefined) => void, context: string): boolean => {
    return safeSetStorage(setter, undefined, context);
};
